import { ChevronRight, ReceiptPercent, ShoppingCart, Tag, TruckFast, XMark } from "@medusajs/icons"
import { Button, Checkbox, FocusModal, IconButton, Input, Prompt, RadioGroup, Tabs, Text, toast } from "@medusajs/ui"
import { useEffect, useMemo, useState, type ReactNode } from "react"
import { Link, useBlocker, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { Field } from "@/components/form"
import { ResourcePicker, useResourceNames } from "@/components/resource-picker"
import { DataTable, EmptyState, ErrorState, PageHeader, SaveBar, SearchInput, Section, Thumb, usePage } from "@/components/ui"
import {
  describeDiscount,
  discountStatus,
  emptyDiscount,
  generateCode,
  KIND_LABELS,
  promotionToDraft,
  saveDiscount,
  useDeleteDiscount,
  useDiscount,
  useDiscounts,
  useSaveDiscount,
  validateDiscount,
  type DiscountDraft,
  type DiscountKind,
} from "@/lib/discounts"
import { useCan } from "@/lib/permissions"
import { errorMessage } from "@/lib/sdk"
import { storefrontUrl } from "@/lib/storefront-url"
import { Textarea } from "@medusajs/ui"

const PAGE = 50
const STATUS_PILL: Record<string, { bg: string; fg: string; label: string }> = {
  active: { bg: "var(--k-ok-bg)", fg: "var(--k-ok-fg)", label: "Active" },
  scheduled: { bg: "var(--k-info-bg-strong)", fg: "var(--k-info-fg-strong)", label: "Scheduled" },
  expired: { bg: "var(--k-fill-strong)", fg: "var(--k-text-muted)", label: "Expired" },
  inactive: { bg: "var(--k-fill-strong)", fg: "var(--k-text-muted)", label: "Deactivated" },
}
const Pill = ({ s }: { s: string }) => <span className="tf-pill" style={{ background: STATUS_PILL[s].bg, color: STATUS_PILL[s].fg }}>{STATUS_PILL[s].label}</span>

const TYPES: { kind: DiscountKind; icon: any; body: string }[] = [
  { kind: "amount_off_products", icon: Tag, body: "Discount specific products or collections of products" },
  { kind: "buy_x_get_y", icon: ShoppingCart, body: "Discount products based on a customer's purchase" },
  { kind: "amount_off_order", icon: ReceiptPercent, body: "Discount the total order amount" },
  { kind: "free_shipping", icon: TruckFast, body: "Offer free shipping on an order" },
]

export function DiscountsPage() {
  const [params, setParams] = useSearchParams()
  const { pageIndex, q, setPage, setQ } = usePage(params, setParams)
  const tab = params.get("status") ?? ""
  // Status is derived (dates, usage limit), so tabs filter client-side over a wider window.
  const { data: raw, isLoading, error } = useDiscounts({ q, limit: tab ? 200 : PAGE, offset: tab ? 0 : pageIndex * PAGE })
  const data = useMemo(() => {
    if (!raw || !tab) return raw
    const list = (raw.promotions as any[]).filter((p) => discountStatus(p) === tab)
    return { ...raw, promotions: list.slice(pageIndex * PAGE, pageIndex * PAGE + PAGE), count: list.length }
  }, [raw, tab, pageIndex])
  const setTab = (v: string) => {
    const next = new URLSearchParams(params)
    v ? next.set("status", v) : next.delete("status")
    next.delete("page")
    setParams(next)
  }
  const navigate = useNavigate()
  const can = useCan()
  const [picking, setPicking] = useState(false)

  return (
    <>
      <PageHeader title="Discounts" actions={can("discounts:write") && <Button size="small" onClick={() => setPicking(true)}>Create discount</Button>} />
      <Section>
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <Tabs value={tab} onValueChange={setTab}>
            <Tabs.List>
              {[["", "All"], ["active", "Active"], ["scheduled", "Scheduled"], ["expired", "Expired"]].map(([v, l]) => (
                <Tabs.Trigger key={v} value={v}>{l}</Tabs.Trigger>
              ))}
            </Tabs.List>
          </Tabs>
          <SearchInput value={q} onChange={setQ} placeholder="Search discounts" />
        </div>
        {error ? (
          <ErrorState error={error} />
        ) : (
          <DataTable
            rowKey={(p: any) => p.id}
            rows={data?.promotions as any[]}
            count={data?.count ?? 0}
            pageSize={PAGE}
            pageIndex={pageIndex}
            onPageChange={setPage}
            loading={isLoading}
            onRowClick={(p: any) => navigate(`/discounts/${p.id}`)}
            empty={
              <EmptyState
                title="Manage discounts and promotions"
                body="Create discount codes and automatic discounts that apply at checkout."
                action={can("discounts:write") && <Button size="small" onClick={() => setPicking(true)}>Create discount</Button>}
              />
            }
            mobile={(p: any) => {
              const d = promotionToDraft(p)
              return (
                <div className="flex flex-col gap-y-1">
                  <div className="flex items-center justify-between gap-x-2">
                    <span className="truncate text-[13px] font-medium">{p.code}</span>
                    <Pill s={discountStatus(p)} />
                  </div>
                  <span className="text-[12px] text-ui-fg-subtle">{describeDiscount(d)[0]}</span>
                  <span className="text-[12px] text-ui-fg-muted">{p.is_automatic ? "Automatic" : "Code"} · {KIND_LABELS[d.kind]} · {Number(p.used ?? 0)} used</span>
                </div>
              )
            }}
            columns={[
              {
                key: "title",
                header: "Title",
                cell: (p: any) => (
                  <div className="flex flex-col">
                    <span className="text-[13px] font-medium">{p.code}</span>
                    <span className="text-[12px] text-ui-fg-subtle">{describeDiscount(promotionToDraft(p))[0]}</span>
                  </div>
                ),
              },
              { key: "status", header: "Status", cell: (p: any) => <Pill s={discountStatus(p)} /> },
              { key: "method", header: "Method", cell: (p: any) => (p.is_automatic ? "Automatic" : "Code") },
              { key: "type", header: "Type", cell: (p: any) => KIND_LABELS[promotionToDraft(p).kind] },
              { key: "used", header: "Used", cell: (p: any) => `${Number(p.used ?? 0)}${p.limit != null ? ` / ${p.limit}` : ""}` },
            ]}
          />
        )}
      </Section>

      <FocusModal open={picking} onOpenChange={setPicking}>
        <FocusModal.Content className="mx-auto my-auto h-auto max-h-[90vh] w-[min(560px,calc(100vw-32px))] overflow-hidden rounded-xl">
          <FocusModal.Header>
            <span className="text-[14px] font-semibold">Select discount type</span>
          </FocusModal.Header>
          <ul className="divide-y">
            {TYPES.map((t) => {
              const Icon = t.icon
              return (
                <li key={t.kind}>
                  <Link to={`/discounts/new?type=${t.kind}`} onClick={() => setPicking(false)} className="flex items-center gap-x-3 px-4 py-3 hover:bg-ui-bg-base-hover">
                    <span className="flex size-9 items-center justify-center rounded-lg bg-ui-bg-component"><Icon /></span>
                    <div className="flex-1">
                      <Text size="small" weight="plus">{KIND_LABELS[t.kind]}</Text>
                      <Text size="xsmall" className="text-ui-fg-subtle">{t.body}</Text>
                    </div>
                    <ChevronRight className="text-ui-fg-muted" />
                  </Link>
                </li>
              )
            })}
          </ul>
        </FocusModal.Content>
      </FocusModal>
    </>
  )
}

// ── Editor ─────────────────────────────────────────────────────────────────

export function DiscountEditorPage() {
  const { id: routeId } = useParams()
  const [params] = useSearchParams()
  const id = !routeId || routeId === "new" ? null : routeId
  const { data, error, isLoading } = useDiscount(id)
  if (error) return <ErrorState error={error} />
  if (id && (isLoading || !data)) return null
  const kind = (params.get("type") as DiscountKind) || "amount_off_order"
  return <Editor key={id ?? kind} id={id} promotion={data?.promotion ?? null} initial={data?.draft ?? emptyDiscount(KIND_LABELS[kind] ? kind : "amount_off_order")} />
}

function Editor({ id, promotion, initial }: { id: string | null; promotion: any | null; initial: DiscountDraft }) {
  const navigate = useNavigate()
  const canWrite = useCan()("discounts:write")
  const base = useMemo(() => initial, [initial])
  const [d, setD] = useState(base)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const save = useSaveDiscount()
  const del = useDeleteDiscount()
  const dirty = JSON.stringify(d) !== JSON.stringify(base)
  useEffect(() => setD(base), [base])
  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty && !save.isPending && currentLocation.pathname !== nextLocation.pathname)
  const set = <K extends keyof DiscountDraft>(k: K, v: DiscountDraft[K]) => setD((x) => ({ ...x, [k]: v }))

  const submit = (e?: React.FormEvent) => {
    e?.preventDefault()
    const problem = validateDiscount(d)
    if (problem) return toast.error(problem)
    save.mutate(
      { draft: d, existing: promotion },
      {
        onSuccess: (newId) => {
          toast.success(id ? "Discount saved" : "Discount created")
          if (!id) navigate(`/discounts/${newId}`, { replace: true })
        },
        onError: (err) => toast.error(errorMessage(err)),
      }
    )
  }
  const status = promotion ? discountStatus({ ...promotion, status: d.active ? "active" : "inactive" }) : null

  return (
    <form onSubmit={submit} className="pb-24">
      <PageHeader
        back="/discounts"
        title={<span className="flex items-center gap-x-2">{id ? promotion.code : `Create discount: ${KIND_LABELS[d.kind]}`} {status && <Pill s={status} />}</span>}
        actions={
          id &&
          canWrite && (
            <>
              <Button type="button" size="small" variant="secondary" onClick={() => save.mutate({ draft: { ...d, active: !d.active }, existing: promotion }, { onSuccess: () => (toast.success(d.active ? "Discount deactivated" : "Discount activated")), onError: (e) => toast.error(errorMessage(e)) })}>
                {d.active ? "Deactivate" : "Activate"}
              </Button>
              <Button type="button" size="small" variant="secondary" onClick={() => setConfirmDelete(true)}>Delete</Button>
              <Button
                type="button"
                size="small"
                variant="secondary"
                onClick={async () => {
                  try {
                    const newId = await saveDiscount({ ...d, code: `${d.code}-COPY`.slice(0, 40), active: false }, null)
                    toast.success("Discount duplicated (deactivated)")
                    navigate(`/discounts/${newId}`)
                  } catch (e) {
                    toast.error(errorMessage(e))
                  }
                }}
              >
                Duplicate
              </Button>
            </>
          )
        }
      />
      <fieldset disabled={!canWrite} className="grid gap-4 lg:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
          <Section title={KIND_LABELS[d.kind]}>
            <div className="flex flex-col gap-y-3 px-4 py-3">
              <Text size="small" weight="plus">Method</Text>
              <div className="inline-flex w-fit rounded-lg bg-ui-bg-component p-0.5">
                {(["code", "automatic"] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    disabled={!!id}
                    onClick={() => set("method", m)}
                    className={"rounded-md px-3 py-1 text-[13px] font-medium " + (d.method === m ? "bg-ui-bg-base shadow-elevation-card-rest" : "text-ui-fg-subtle")}
                  >
                    {m === "code" ? "Discount code" : "Automatic discount"}
                  </button>
                ))}
              </div>
              {d.method === "code" ? (
                <Field label="Discount code" hint="Customers must enter this code at checkout.">
                  <div className="flex gap-2">
                    <Input value={d.code} maxLength={40} onChange={(e) => set("code", e.target.value.toUpperCase().replace(/\s/g, ""))} className="flex-1" />
                    <Button type="button" size="small" variant="secondary" onClick={() => set("code", generateCode())}>Generate</Button>
                  </div>
                </Field>
              ) : (
                <Field label="Title" hint="Customers see this in their cart and at checkout.">
                  <Input value={d.code} maxLength={40} onChange={(e) => set("code", e.target.value)} />
                </Field>
              )}
            </div>
          </Section>

          {(d.kind === "amount_off_products" || d.kind === "amount_off_order") && (
            <Section title="Discount value">
              <div className="flex flex-col gap-y-3 px-4 py-3">
                <div className="flex flex-wrap items-end gap-2">
                  <div className="inline-flex rounded-lg bg-ui-bg-component p-0.5">
                    {(["percentage", "fixed"] as const).map((t) => (
                      <button key={t} type="button" onClick={() => set("value_type", t)} className={"rounded-md px-3 py-1 text-[13px] font-medium " + (d.value_type === t ? "bg-ui-bg-base shadow-elevation-card-rest" : "text-ui-fg-subtle")}>
                        {t === "percentage" ? "Percentage" : "Fixed amount"}
                      </button>
                    ))}
                  </div>
                  <div className="relative w-40">
                    <span className="pointer-events-none absolute top-1/2 z-10 -translate-y-1/2 text-[13px] text-ui-fg-muted" style={d.value_type === "percentage" ? { right: 10 } : { left: 10 }}>{d.value_type === "percentage" ? "%" : "$"}</span>
                    <Input aria-label="Discount value" inputMode="decimal" value={d.value} className={d.value_type === "percentage" ? "pr-7" : "pl-6"} onChange={(e) => set("value", e.target.value.replace(/[^0-9.]/g, ""))} />
                  </div>
                </div>
                {d.kind === "amount_off_products" && (
                  <>
                    <Text size="small" weight="plus">Applies to</Text>
                    <RadioGroup value={d.applies_to} onValueChange={(v) => set("applies_to", v as any)} className="flex flex-col gap-y-2">
                      {[
                        ["all", "All products"],
                        ["collections", "Specific collections"],
                        ["products", "Specific products"],
                      ].map(([v, l]) => (
                        <label key={v} className="flex items-center gap-x-2"><RadioGroup.Item value={v} /><Text size="small">{l}</Text></label>
                      ))}
                    </RadioGroup>
                    {d.applies_to === "collections" && <Picked kind="collections" ids={d.collection_ids} onChange={(v) => set("collection_ids", v)} />}
                    {d.applies_to === "products" && <Picked kind="products" ids={d.product_ids} onChange={(v) => set("product_ids", v)} />}
                  </>
                )}
              </div>
            </Section>
          )}

          {d.kind === "buy_x_get_y" && (
            <>
              <Section title="Customer buys">
                <div className="flex flex-col gap-y-3 px-4 py-3">
                  <div className="max-w-[180px]">
                    <Field label="Minimum quantity of items"><Input inputMode="numeric" value={d.buy_quantity} onChange={(e) => set("buy_quantity", e.target.value.replace(/\D/g, ""))} /></Field>
                  </div>
                  <Text size="small" weight="plus">Any items from</Text>
                  <Picked kind="products" ids={d.buy_product_ids} onChange={(v) => set("buy_product_ids", v)} />
                </div>
              </Section>
              <Section title="Customer gets">
                <div className="flex flex-col gap-y-3 px-4 py-3">
                  <Text size="small" className="text-ui-fg-subtle">Customers must add the quantity of items specified below to their cart.</Text>
                  <div className="max-w-[180px]">
                    <Field label="Quantity"><Input inputMode="numeric" value={d.get_quantity} onChange={(e) => set("get_quantity", e.target.value.replace(/\D/g, ""))} /></Field>
                  </div>
                  <Text size="small" weight="plus">Any items from</Text>
                  <Picked kind="products" ids={d.get_product_ids} onChange={(v) => set("get_product_ids", v)} />
                  <Text size="small" weight="plus">At a discounted value</Text>
                  <RadioGroup value={d.get_value_type} onValueChange={(v) => set("get_value_type", v as any)} className="flex flex-col gap-y-2">
                    <label className="flex items-center gap-x-2"><RadioGroup.Item value="percentage" /><Text size="small">Percentage</Text></label>
                    {d.get_value_type === "percentage" && (
                      <div className="relative ml-6 w-32">
                        <Input aria-label="Percentage off" inputMode="decimal" value={d.get_value} className="pr-7" onChange={(e) => set("get_value", e.target.value.replace(/[^0-9.]/g, ""))} />
                        <span className="pointer-events-none absolute right-2.5 top-1/2 z-10 -translate-y-1/2 text-[13px] text-ui-fg-muted">%</span>
                      </div>
                    )}
                    <label className="flex items-center gap-x-2"><RadioGroup.Item value="free" /><Text size="small">Free</Text></label>
                  </RadioGroup>
                </div>
              </Section>
            </>
          )}

          {d.kind !== "buy_x_get_y" && (
            <Section title="Minimum purchase requirements">
              <div className="flex flex-col gap-y-2 px-4 py-3">
                <RadioGroup value={d.min_type} onValueChange={(v) => set("min_type", v as any)} className="flex flex-col gap-y-2">
                  {[
                    ["none", "No minimum requirements"],
                    ["amount", "Minimum purchase amount ($)"],
                    ["quantity", "Minimum quantity of items"],
                  ].map(([v, l]) => (
                    <div key={v}>
                      <label className="flex items-center gap-x-2"><RadioGroup.Item value={v} /><Text size="small">{l}</Text></label>
                      {d.min_type === v && v !== "none" && (
                        <div className="ml-6 mt-2 w-40">
                          <Input aria-label={v === "amount" ? "Minimum amount in dollars" : "Minimum number of items"} inputMode="decimal" value={d.min_value} onChange={(e) => set("min_value", e.target.value.replace(/[^0-9.]/g, ""))} />
                          {v === "quantity" && <Text size="xsmall" className="mt-1 text-ui-fg-muted">Counts the quantity of a single line item.</Text>}
                        </div>
                      )}
                    </div>
                  ))}
                </RadioGroup>
              </div>
            </Section>
          )}

          {d.kind !== "free_shipping" && (
            <Section title="Purchase type">
              <div className="flex flex-col gap-y-2 px-4 py-3">
                <RadioGroup value={d.purchase_type} onValueChange={(v) => set("purchase_type", v as any)} className="flex flex-col gap-y-2">
                  {[
                    ["one_time", "One-time purchase"],
                    ["subscription", "Subscription"],
                    ["both", "Both"],
                  ].map(([v, l]) => (
                    <label key={v} className="flex items-center gap-x-2"><RadioGroup.Item value={v} /><Text size="small">{l}</Text></label>
                  ))}
                </RadioGroup>
                <Text size="xsmall" className="text-ui-fg-muted">Applies the discount only to items bought that way. Subscribe-and-save items are subscriptions.</Text>
              </div>
            </Section>
          )}

          <Section title="Customer eligibility">
            <div className="flex flex-col gap-y-2 px-4 py-3">
              <RadioGroup value={d.eligibility} onValueChange={(v) => set("eligibility", v as any)} className="flex flex-col gap-y-2">
                <label className="flex items-center gap-x-2"><RadioGroup.Item value="all" /><Text size="small">All customers</Text></label>
                <label className="flex items-center gap-x-2"><RadioGroup.Item value="customers" /><Text size="small">Specific customers</Text></label>
              </RadioGroup>
              {d.eligibility === "customers" && (
                <div className="ml-6">
                  <Textarea
                    aria-label="Customer emails"
                    rows={3}
                    placeholder="One email per line"
                    defaultValue={d.customer_emails.join("\n")}
                    onBlur={(e) => set("customer_emails", [...new Set(e.target.value.split(/[\s,;]+/).map((x) => x.trim()).filter((x) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(x)))].slice(0, 500))}
                  />
                  <Text size="xsmall" className="mt-1 text-ui-fg-muted">Matched to the email entered at checkout. {d.customer_emails.length} customer{d.customer_emails.length === 1 ? "" : "s"}.</Text>
                </div>
              )}
            </div>
          </Section>

          <Section title="Combinations">
            <div className="flex flex-col gap-y-2 px-4 py-3">
              <Text size="small" className="text-ui-fg-subtle">This discount can be combined with:</Text>
              {(
                [
                  ["product", "Product discounts"],
                  ["order", "Order discounts"],
                  ["shipping", "Shipping discounts"],
                ] as const
              ).map(([k, l]) => (
                <label key={k} className="flex items-center gap-x-2">
                  <Checkbox checked={d.combines[k]} onCheckedChange={(c) => set("combines", { ...d.combines, [k]: c === true })} />
                  <Text size="small">{l}</Text>
                </label>
              ))}
              <Text size="xsmall" className="text-ui-fg-muted">Two discounts stack only when each allows the other's type.</Text>
            </div>
          </Section>

          <Section title="Maximum discount uses">
            <div className="flex flex-col gap-y-2 px-4 py-3">
              <label className="flex items-center gap-x-2">
                <Checkbox checked={d.limit_total} onCheckedChange={(c) => set("limit_total", c === true)} />
                <Text size="small">Limit number of times this discount can be used in total</Text>
              </label>
              {d.limit_total && (
                <div className="ml-6 w-40"><Input aria-label="Usage limit" inputMode="numeric" value={d.limit} onChange={(e) => set("limit", e.target.value.replace(/\D/g, ""))} /></div>
              )}
              <label className="flex items-center gap-x-2">
                <Checkbox checked={d.once_per_customer} onCheckedChange={(c) => set("once_per_customer", c === true)} />
                <Text size="small">Limit to one use per customer</Text>
              </label>
              {promotion && <Text size="xsmall" className="text-ui-fg-muted">Used {Number(promotion.used ?? 0)} time{Number(promotion.used ?? 0) === 1 ? "" : "s"}.</Text>}
            </div>
          </Section>

          <Section title="Active dates">
            <div className="grid gap-3 px-4 py-3 sm:grid-cols-2">
              <Field label="Start date and time"><Input type="datetime-local" value={d.starts_at} onChange={(e) => set("starts_at", e.target.value)} /></Field>
              <div className="flex flex-col justify-end gap-2">
                <label className="flex items-center gap-x-2">
                  <Checkbox checked={d.has_end} onCheckedChange={(c) => set("has_end", c === true)} />
                  <Text size="small">Set end date</Text>
                </label>
              </div>
              {d.has_end && <Field label="End date and time"><Input type="datetime-local" value={d.ends_at} min={d.starts_at} onChange={(e) => set("ends_at", e.target.value)} /></Field>}
            </div>
          </Section>
        </div>

        <div className="flex min-w-0 flex-col gap-4">
          <Section title="Summary">
            <div className="px-4 py-3">
              <Text size="small" weight="plus" className="break-all">{d.code || (d.method === "code" ? "No discount code yet" : "No title yet")}</Text>
              <Text size="xsmall" className="mb-3 text-ui-fg-subtle">{d.method === "code" ? "Code" : "Automatic"} · {KIND_LABELS[d.kind]}</Text>
              <Text size="small" weight="plus">Details</Text>
              <ul className="mt-1 list-disc pl-5 text-[13px] text-ui-fg-subtle">
                {describeDiscount(d).map((l) => <li key={l}>{l}</li>)}
              </ul>
            </div>
          </Section>
          {id && d.method === "code" && d.code && (
            <Section title="Shareable link">
              <div className="flex flex-col gap-2 px-4 py-3">
                <Text size="xsmall" className="text-ui-fg-subtle">Opening this link applies the code to the shopper's cart automatically.</Text>
                <code className="break-all rounded-md bg-ui-bg-subtle px-2 py-1.5 text-[12px]">{`${storefrontUrl()}/discount/${encodeURIComponent(d.code.toUpperCase())}`}</code>
                <Button
                  type="button"
                  size="small"
                  variant="secondary"
                  onClick={() =>
                    navigator.clipboard
                      .writeText(`${storefrontUrl()}/discount/${encodeURIComponent(d.code.toUpperCase())}`)
                      .then(() => toast.success("Link copied"))
                      .catch(() => toast.error("Couldn't copy the link"))
                  }
                >
                  Copy link
                </Button>
              </div>
            </Section>
          )}
        </div>
      </fieldset>

      {canWrite && <SaveBar visible={dirty || !id} message={id ? "Unsaved changes" : "Unsaved discount"} saving={save.isPending} onDiscard={() => (id ? setD(base) : navigate("/discounts"))} />}

      <Prompt open={blocker.state === "blocked"}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Leave page with unsaved changes?</Prompt.Title>
            <Prompt.Description>Leaving this page will discard all unsaved changes.</Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel onClick={() => blocker.reset?.()}>Stay</Prompt.Cancel>
            <Prompt.Action onClick={() => blocker.proceed?.()}>Leave page</Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
      <Prompt open={confirmDelete} onOpenChange={setConfirmDelete}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Delete {promotion?.code}?</Prompt.Title>
            <Prompt.Description>Customers won't be able to use it any more. Orders that used it keep their discount.</Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Cancel</Prompt.Cancel>
            <Prompt.Action onClick={() => del.mutate(promotion, { onSuccess: () => (setD(base), toast.success("Discount deleted"), navigate("/discounts", { replace: true })), onError: (e) => toast.error(errorMessage(e)) })}>
              Delete discount
            </Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </form>
  )
}

function Picked({ kind, ids, onChange }: { kind: "products" | "collections"; ids: string[]; onChange: (ids: string[]) => void }): ReactNode {
  const [open, setOpen] = useState(false)
  const names = useResourceNames(kind, ids)
  return (
    <div className="flex flex-col gap-2">
      <Button type="button" size="small" variant="secondary" className="w-fit" onClick={() => setOpen(true)}>
        Browse {kind}
      </Button>
      {ids.length > 0 && (
        <ul className="divide-y rounded-lg border">
          {ids.map((id) => (
            <li key={id} className="flex items-center gap-x-3 px-3 py-2">
              <Thumb src={names.data?.[id]?.image} size={32} />
              <Text size="small" className="flex-1 truncate">{names.data?.[id]?.title ?? "…"}</Text>
              <IconButton type="button" size="small" variant="transparent" aria-label="Remove" onClick={() => onChange(ids.filter((x) => x !== id))}>
                <XMark />
              </IconButton>
            </li>
          ))}
        </ul>
      )}
      {open && <ResourcePicker kind={kind} selected={ids} onDone={onChange} onClose={() => setOpen(false)} />}
    </div>
  )
}
